import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Radio, Segment } from 'tea-component';
import { getGameList } from '@/pages/benchmark/services/benchmark';
import { getName } from '@home/utils/common';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { IFilterOption } from '../useData';
import classNames from 'classnames';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import TransferModalSelector from '@home/components/TransferModalSelector';
import BasicImage from '@home/components/BasicImage';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import { throttle, uniqBy } from 'lodash-es';
import { ITransferModalRef } from '@home/components/TransferModal/type';
import { highlightText } from '@/pages/dashboard/components/FilterSelect/utils';
import './index.scss';

export interface IGenreFilterProps {
  options: IFilterOption[];
  value?: string;
  frameType: string;
  isEN: boolean;
  logConfig?: {
    typeId: string;
    pageId: string;
  };
  optionType?: string;
  gameList?: any;
  isError?: boolean;
  setIsGameError?: any;
  onChange?: (value: any, type: string) => void;
}

const PLEASE_CHOOSE_GAME_EN = 'Please choose games';
const PLEASE_CHOOSE_GAME_CN = '请选择游戏';

const GenreFilter = (props: IGenreFilterProps) => {
  const {
    options,
    value: propsValue,
    frameType,
    isEN,
    optionType,
    gameList,
    isError,
    logConfig,
    setIsGameError,
    onChange,
  } = props;

  // 当前选项
  const [activeSegment, setActiveSegment] = useState<string>('shooter');

  // 筛选器类型：genre、game
  const [type, setType] = useState<string>('genre');

  /** * 左侧游戏 */
  const [leftGameList, setLeftGameList] = useState<any[]>([]);

  // 右侧选中游戏
  const [rightGameList, setRightGameList] = useState<any[]>([]);

  // 游戏筛选框的值
  const [gameValue, setGameValue] = useState<any[]>([]);

  // 搜索浮窗提示列表
  const [searchGameList, setSearchGameList] = useState<any[]>([]);

  const [leftLoading, setLeftLoading] = useState(false);

  const [rightLoading, setRightLoading] = useState(false);

  // 快捷选项值
  const [quickValue, setQuickValue] = useState<string[]>([]);

  const gameSelectorRef = useRef<ITransferModalRef>(null);

  // 缓存打开浮窗前的快捷选项value
  const oldQuickValue = useRef<string[]>([]);

  // 品类change
  const handleSegmentChange = (value: string) => {
    setActiveSegment(value);
    onChange?.(value, 'genre');
  };

  useEffect(() => {
    if (gameList) {
      setGameValue(gameList);
      setRightGameList(gameList);
      getInitGame(gameList);
    }
  }, [gameList]);

  useEffect(() => {
    if (propsValue) {
      setActiveSegment(propsValue);
    }
  }, [propsValue]);

  useEffect(() => {
    if (optionType) {
      setType(optionType);
    }
  }, [optionType]);

  // 组装品类、游戏快捷选项数据
  const { gameSegmentList, quickOptions } = useMemo(() => {
    const quickOptions: any = [];
    const gameSegmentList = options?.map((item) => {

      const { name_en, name_ch, code } = item;
      const text = getName(isEN, name_en, name_ch);

      // 过滤all选项
      if(code !== '255'){
        quickOptions.push({ value: code, textEn: name_en, textCn: name_ch });
      }

      return {
        ...item,
        value: code,
        text,
      };
    });
    return {
      gameSegmentList,
      quickOptions,
    };
  }, [isEN, options]);

  // 按钮打点
  const logPoint = (buttonId: string, extInfo?: any) => {
    const { typeId, pageId } = logConfig || {};
    if (pageId && typeId) {
      const logObj = {
        buttonId,
        extInfo,
        pageId,
        uidType: '', // 基准打点不包含uidType
        typeId,
      };
      buriedBtnPoint(logObj);
    }
  };

  // 同步左侧勾选状态
  const awaitLeftGameChekced = (leftGames: any, rightGames?: any) => {
    const rightGameList =
      rightGames || gameSelectorRef.current?.getRightOptions() || [];

    const newLeftGame = leftGames.map((game: any) => {
      const newGame = { ...game };
      if (rightGameList.some((item: any) => item?.game_id === game?.game_id)) {
        newGame.checked = true;
      } else {
        newGame.checked = false;
      }
      return newGame;
    });
    setLeftGameList(newLeftGame);
  };

  // 删除右侧游戏
  const onRemoveRightGame = (game: any) => {
    const rightGameList = gameSelectorRef.current?.getRightOptions() || [];
    const filterRightOptions = rightGameList.filter(
      (item: any) => item?.game_id !== game?.game_id,
    );
    leftGameList.forEach((item) => {
      if (item?.game_id === game?.game_id) {
        item.checked = false;
      }
    });
    setLeftGameList([...leftGameList]);
    setRightGameList(filterRightOptions);
    if (filterRightOptions.length === 0) {
      setQuickValue([]);
    }
  };

  const getGameRender = (
    item: any,
    hasClose = false,
    style?: React.CSSProperties,
  ) => {
    const { release_time, publisher, game_name, cover } = item;
    return (
      <div
        className={classNames('dbb-game-transfer-row', {
          'right-game': hasClose,
        })}
        style={style}
      >
        <BasicImage
          src={cover === 'webp' ? GameDefault : cover}
          errorSrc={GameDefault}
        />
        <div className="dbb-game-transfer-info">
          <div className="dbb-game-transfer-row-title">{game_name}</div>
          <div className="dbb-game-transfer-row-text">
            <span className="dbb-game-transfer-row-date">
              {`${getName(isEN, 'Release', '发布日期')} ${dateTimeFormat(
                release_time,
                'day',
              )}`}
            </span>
            <span className="dbb-game-transfer-row-publisher">
              <span className="dbb-game-transfer-row-publisher-text">
                {publisher || '-'}
              </span>
            </span>
          </div>
        </div>
        {hasClose && (
          <div className="dbh-game-transfer-close">
            <Icon type="dismiss" onClick={() => onRemoveRightGame(item)} />
          </div>
        )}
      </div>
    );
  };

  // 获取默认游戏列表
  const getDefaultGame = () => {
    setLeftLoading(true);
    getGameList({
      frame_type: frameType,
      is_page: true,
      page: 1,
      page_size: 1000,
    })
      .then((data) => {
        const leftOptionList = data?.list?.map((item: any) => ({
          ...item,
          render: getGameRender,
        }));
        awaitLeftGameChekced(leftOptionList);
      })
      .finally(() => {
        setLeftLoading(false);
      });
  };

  // 搜索框值变化
  const onSearchValueChange = throttle((value?: string) => {
    if (!value) {
      setSearchGameList([]);
      getDefaultGame();
    }
    getGameList({
      frame_type: frameType,
      is_page: true,
      page: 1,
      page_size: 100,
      keywords: value,
    })
      .then((data) => {
        const searchOptionList = data?.list?.map((item: any) => {
          const { game_id, game_name } = item || {};
          return {
            value: game_id,
            text: (
              <div
                dangerouslySetInnerHTML={{
                  __html: highlightText(game_name, value || ''),
                }}
              />
            ),
            name: game_name,
          };
        });
        setSearchGameList(searchOptionList);
      })
      .finally(() => {});
  }, 1000);

  // 搜索回车
  const onSearch = (value?: string) => {
    if (!value) {
      setSearchGameList([]);
      getDefaultGame();
      return;
    }
    setLeftLoading(true);
    getGameList({
      frame_type: frameType,
      is_page: true,
      page: 1,
      page_size: 1000,
      keywords: value,
    })
      .then((data) => {
        const list = data?.list?.map((item: any) => ({
          ...item,
          render: getGameRender,
        }));
        awaitLeftGameChekced(list);
      })
      .finally(() => {
        setLeftLoading(false);
      });
  };

  // 快捷选项
  const changeQuickOption = (
    value: string,
    checked: boolean,
    values: string[],
  ) => {
    const rightGameList = gameSelectorRef.current?.getRightOptions() || [];
    setQuickValue(values);
    if (!checked) {
      setRightLoading(true);
      getGameList({
        frame_type: frameType,
        is_page: true,
        page: 1,
        page_size: 10000,
        filters: {
          genre_list: [value],
        },
      })
        .then((data) => {
          const gameList = data?.list || [];
          const list = uniqBy([...rightGameList, ...gameList], 'game_id');
          awaitLeftGameChekced(leftGameList, list);
          setRightGameList(list);
        })
        .finally(() => {
          setRightLoading(false);
        });
    } else {
      const gameList = rightGameList.filter(
        (item: any) => item.game_genre !== value,
      );
      awaitLeftGameChekced(leftGameList, gameList);
      setRightGameList(gameList);
    }
  };

  const onConfirm = (options: any) => {
    setGameValue(options);
    onChange?.(options, 'game');
    getInitGame(options);
    if (options.length > 0) {
      setIsGameError(false);
    } else {
      setIsGameError(true);
    }
    logPoint('800201003', String(options?.length));
  };

  // 返回需要重置左/右侧选项
  const onCancel = () => {
    // 取消需要返回快捷选项选中状态
    const newRightOptions = gameSelectorRef.current?.getRightOptions() || [];
    if (newRightOptions?.length !== gameValue?.length) {
      setQuickValue(oldQuickValue.current);
    }
    getInitGame();
    setRightGameList([]);
  };

  const changeType = (type: string) => {
    setType(type);
    onChange?.(type === 'game' ? gameValue : activeSegment, type);
  };

  const getInitGame = (valueOptions?: any) => {
    setLeftLoading(true);
    getGameList({
      frame_type: frameType,
      is_page: true,
      page: 1,
      page_size: 1000,
    })
      .then((data) => {
        const leftOptionList = data?.list?.map((item: any) => ({
          ...item,
          render: getGameRender,
        }));
        awaitLeftGameChekced(leftOptionList, valueOptions || gameValue);
      })
      .finally(() => {
        setLeftLoading(false);
      });
  };

  const onModalOpen = () => {
    // 缓存每次打开浮窗前的快捷选项，方便取消时回滚状态
    oldQuickValue.current = quickValue;
  };

  return (
    <div
      className="dbb-benchmark-segment-setting dbb-genre-benchmark-filter-col"
      style={{ height: 42 }}
    >
      <div className={classNames({ 'is-en': isEN })}>
        {getName(isEN, 'Game/Genre', '游戏/品类')}
      </div>
      <Radio.Group
        value={type}
        onChange={changeType}
        style={{ marginRight: 20 }}
      >
        <Radio name="game">{getName(isEN, 'Game', '游戏')}</Radio>
        <Radio name="genre">{getName(isEN, 'Genre', '品类')}</Radio>
      </Radio.Group>
      {type === 'genre' && (
        <Segment
          className="dbb-benchmark-segment"
          options={gameSegmentList}
          value={activeSegment}
          onChange={(value) => handleSegmentChange(value)}
        />
      )}
      {type === 'game' && (
        <div
          className={classNames({
            'dbb-genre-benchmark-filter-error': isError,
          })}
        >
          <TransferModalSelector
            ref={gameSelectorRef}
            primaryKey="game_id"
            primaryName="game_name"
            maxLength={10000}
            leftOptions={leftGameList}
            rightOptions={rightGameList}
            searchOptions={searchGameList}
            leftLoading={leftLoading}
            rightLoading={rightLoading}
            buttonTextCn="游戏"
            buttonTextEn="Games"
            titleEn="Select Games"
            titleCn="选择游戏"
            searchPlaceholderEn="Search any game"
            searchPlaceholderCn="搜索任何游戏"
            rightSubTitleEn="Games"
            rightSubTitleCn="游戏"
            rightEmptyTextEn="Please select a game"
            rightEmptyTextCn="请选择游戏"
            leftEmptyTextEn="No search results"
            leftEmptyTextCn="没有搜索结果"
            loadingTextEn='loading...'
            loadingTextCn='加载中...'
            quickOptions={quickOptions}
            quickValue={quickValue}
            virtual
            rowHeight={65}
            leftListHeight={345}
            rightListHeight={283}
            rightOptionRender={(item, style) =>
              getGameRender(item, true, style)
            }
            onSearchValueChange={onSearchValueChange}
            onSearch={onSearch}
            onChangeQuickOptions={changeQuickOption}
            onConfirm={onConfirm}
            onCancel={onCancel}
            value={gameValue}
            buttonStyle={{ minWidth: 366 }}
            onTransferModalOpen={onModalOpen}
          />
          {isError && (
            <div
              className={classNames('dbb-genre-benchmark-filter-error-text', {
                'is-en': isEN,
              })}
            >
              {getName(isEN, PLEASE_CHOOSE_GAME_EN, PLEASE_CHOOSE_GAME_CN)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GenreFilter;
